import { Link } from '@tanstack/react-router';
import { lazy, Suspense, useState, type ReactElement } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formaDelTrimestre } from '@selflevel/domain';
import { Marca } from '@selflevel/ui';
import { db, type Goal } from '../store/db';
import { useAreas, useGoals, useIdentities, useToday } from '../store/hooks';
import {
  setGoalMilestoneDone,
  setGoalStatus,
  upsertGoal,
  upsertGoalMilestone,
  upsertIdentity,
} from '../store/mutations';
import { Bar, Card, Empty, Menu, Select, Sheet, fechaLarga } from '../ui/primitivas';
import { IconCheck, IconMas } from '../ui/iconos';

/** Sueños y trofeos se cargan aparte: Metas no paga por ellos en el arranque. */
const SeccionSuenos = lazy(() => import('./suenos').then((m) => ({ default: m.SeccionSuenos })));
const Trofeo = lazy(() => import('./trofeo').then((m) => ({ default: m.Trofeo })));

/**
 * METAS — y, encima de ellas, la identidad.
 *
 * La jerarquía del producto es Área → **Identidad** → Meta → Hábito, y esta
 * pantalla es donde se ve entera. La identidad va arriba porque es la tesis:
 * una meta cumplida se agota el día que se cumple; una identidad declarada se
 * sostiene con evidencia todos los días.
 *
 * La frase —«soy alguien que…»— **la escribe el usuario y no sale nunca de su
 * dispositivo**. No viaja en la tarjeta de compartir del rewind, no entra en el
 * contexto de la IA sin permiso explícito y no aparece en ninguna estadística
 * agregada. Es el dato más personal de la app.
 */
export function Metas(): ReactElement {
  const areas = useAreas();
  const goals = useGoals();
  const identities = useIdentities();
  const [editor, setEditor] = useState<{ areaId: string; goal?: Goal } | null>(null);
  const [editandoIdentidad, setEditandoIdentidad] = useState<string | null>(null);

  return (
    <main className="screen">
      <header className="topbar glass-2">
        <h1 className="t-title-3 grow">Metas</h1>
        {/* La puerta a las áreas: añadir, renombrar, archivar y ordenar lo que
            cuelga de cada una. Vive aquí porque Metas es el rumbo, y las áreas
            son de dónde sale el rumbo. */}
        <Link to="/areas" className="btn btn--sm">
          Áreas
        </Link>
      </header>

      {/* SUEÑOS (bloque 2): el rumbo largo, encima de las metas de cada área. */}
      <Suspense fallback={null}>
        <SeccionSuenos />
      </Suspense>

      {areas === undefined ? null : areas.length === 0 ? (
        // Decía «se crean en el alta, o desde Ajustes», y desde Ajustes no se
        // podía crear ninguna. Ahora la frase lleva adonde se crean de verdad.
        <Empty title="Todavía no hay áreas">
          <Link to="/areas" className="enlace">
            Añade la primera
          </Link>{' '}
          y de ella colgarán tus metas.
        </Empty>
      ) : (
        areas.map((area) => {
          const identidad = (identities ?? []).find((i) => i.areaId === area.id);
          const metas = (goals ?? []).filter(
            (g) => g.areaId === area.id && g.status !== 'abandonada',
          );

          return (
            <Card key={area.id} className="stack">
              <div className="row" style={{ ['--area' as string]: `var(${area.colorToken})` }}>
                <span className="habit__disc" style={{ width: 32, height: 32 }}>
                  <Marca id={area.marca} size={18} polarity="area" />
                </span>
                <span className="grow t-title-3">{area.name}</span>
              </div>

              {/* La identidad, en primera persona y editable de un toque. */}
              <button
                type="button"
                className="btn btn--ghost"
                style={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  height: 'auto',
                  padding: 'var(--sp-3)',
                }}
                onClick={() => setEditandoIdentidad(area.id)}
              >
                <span className="stack stack--tight">
                  <span className="t-caption t-dim">Quién quieres ser</span>
                  <span className="t-body-strong">
                    {identidad ? identidad.text : 'Todavía no lo has dicho'}
                  </span>
                </span>
              </button>

              {metas.length === 0 ? (
                <p className="t-sm t-dim">Sin metas. Los hábitos ya sostienen la identidad.</p>
              ) : (
                <ul className="stack stack--tight">
                  {metas.map((goal) => (
                    <FilaMeta
                      key={goal.id}
                      goal={goal}
                      colorArea={area.colorToken}
                      onEditar={() => setEditor({ areaId: area.id, goal })}
                    />
                  ))}
                </ul>
              )}

              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => setEditor({ areaId: area.id })}
              >
                <IconMas size={16} /> Meta
              </button>
            </Card>
          );
        })
      )}

      <EditorMeta
        open={editor !== null}
        areaId={editor?.areaId ?? ''}
        goal={editor?.goal ?? null}
        onClose={() => setEditor(null)}
      />

      <EditorIdentidad
        open={editandoIdentidad !== null}
        areaId={editandoIdentidad ?? ''}
        actual={(identities ?? []).find((i) => i.areaId === editandoIdentidad) ?? null}
        onClose={() => setEditandoIdentidad(null)}
      />
    </main>
  );
}

function FilaMeta({
  goal,
  colorArea,
  onEditar,
}: {
  goal: Goal;
  colorArea: string;
  onEditar: () => void;
}): ReactElement {
  const today = useToday();
  const hitos = useLiveQuery(
    async () =>
      (await db.goalMilestones.where('goalId').equals(goal.id).toArray()).sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [goal.id],
  );
  const [abierto, setAbierto] = useState(false);
  const [nuevo, setNuevo] = useState('');

  const hechos = (hitos ?? []).filter((h) => h.doneAt).length;
  const total = (hitos ?? []).length;
  const cumplida = goal.status === 'cumplida';

  return (
    <li className="stack stack--tight">
      <div className="row">
        <button
          type="button"
          className="habit__check"
          style={{ width: 36 }}
          aria-pressed={cumplida}
          aria-label={cumplida ? `Reabrir ${goal.title}` : `Marcar ${goal.title} como cumplida`}
          onClick={() => void setGoalStatus(goal.id, cumplida ? 'activa' : 'cumplida')}
        >
          <span
            style={{
              width: 22,
              height: 22,
              ...(cumplida
                ? {
                    background: 'var(--accent-surface)',
                    borderColor: 'transparent',
                    color: 'var(--accent-surface-ink)',
                  }
                : {}),
            }}
          >
            {cumplida ? <IconCheck size={12} /> : null}
          </span>
        </button>
        <button
          type="button"
          className="grow btn btn--bare"
          style={{ justifyContent: 'flex-start', color: 'inherit', textAlign: 'left' }}
          onClick={() => setAbierto((v) => !v)}
        >
          <span className="t-body-strong">{goal.title}</span>
        </button>
        {total > 0 ? (
          <span className="t-sm t-dim tnum">
            {hechos}/{total}
          </span>
        ) : null}
        {/* Su trofeo, que se aclara con los sub-hitos. La forma es la del trimestre en
            curso: la definitiva será la del trimestre en que se cumpla. */}
        <Suspense fallback={null}>
          <Trofeo
            forma={formaDelTrimestre(goal.localDate ?? today)}
            colorArea={colorArea}
            progreso={cumplida ? 1 : total === 0 ? 0 : hechos / total}
            anio={(goal.localDate ?? today).slice(0, 4)}
            tamano="mini"
            etiqueta={`Trofeo de «${goal.title}»`}
            clave={goal.id}
          />
        </Suspense>
        {/* Hasta aquí una meta era definitiva: se escribía y ya. */}
        <Menu
          label={goal.title}
          acciones={[
            { texto: 'Editar', onSelect: onEditar },
            {
              texto: cumplida ? 'Reabrir' : 'Marcar como cumplida',
              onSelect: () => void setGoalStatus(goal.id, cumplida ? 'activa' : 'cumplida'),
            },
            {
              texto: 'Retirar de la lista',
              peligro: true,
              // Abandonada, no borrada. Lo que hiciste por ella —los pasos, los
              // puntos— ya ocurrió, y una meta que desaparece se lleva por
              // delante el motivo por el que aquellos días contaron.
              onSelect: () => void setGoalStatus(goal.id, 'abandonada'),
            },
          ]}
        />
      </div>

      {total > 0 ? <Bar progress={total === 0 ? 0 : hechos / total} label={goal.title} /> : null}
      {goal.targetDate ? (
        <span className="t-sm t-dim">para el {fechaLarga(goal.targetDate, true)}</span>
      ) : null}

      {abierto ? (
        <div className="stack stack--tight" style={{ paddingLeft: 'var(--sp-6)' }}>
          {(hitos ?? []).map((hito) => (
            <label key={hito.id} className="row" style={{ minHeight: 36 }}>
              <input
                type="checkbox"
                checked={hito.doneAt !== null}
                onChange={(e) => void setGoalMilestoneDone(hito.id, e.target.checked)}
              />
              <span className="t-sm grow">{hito.title}</span>
            </label>
          ))}
          <div className="row">
            <input
              className="input"
              value={nuevo}
              onChange={(e) => setNuevo(e.target.value)}
              placeholder="Añadir un paso"
              aria-label="Nuevo paso de la meta"
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || nuevo.trim() === '') return;
                void upsertGoalMilestone({ goalId: goal.id, title: nuevo });
                setNuevo('');
              }}
            />
          </div>
          <p className="t-sm t-dim">Cada paso suma 50 puntos. Cumplir la meta, 200.</p>
        </div>
      ) : null}
    </li>
  );
}

function EditorMeta({
  open,
  areaId,
  goal,
  onClose,
}: {
  open: boolean;
  areaId: string;
  goal: Goal | null;
  onClose: () => void;
}): ReactElement {
  return (
    <Sheet open={open} onClose={onClose} title={goal ? 'Editar meta' : 'Nueva meta'}>
      {/* La `key` monta un formulario nuevo por meta. Sin ella, abrir una meta
          después de otra enseñaría el título de la anterior: `useState` solo
          lee su argumento en el primer render. */}
      <FormularioMeta key={goal?.id ?? areaId} areaId={areaId} goal={goal} onClose={onClose} />
    </Sheet>
  );
}

function FormularioMeta({
  areaId,
  goal,
  onClose,
}: {
  areaId: string;
  goal: Goal | null;
  onClose: () => void;
}): ReactElement {
  const [title, setTitle] = useState(goal?.title ?? '');
  const [horizon, setHorizon] = useState<Goal['horizon']>(goal?.horizon ?? 'trimestral');

  return (
    <>
      <div className="stack stack--loose">
        <div className="field">
          <label htmlFor="meta-titulo">¿Qué quieres conseguir?</label>
          <input
            id="meta-titulo"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Correr 10 km del tirón"
          />
        </div>
        <div className="field">
          <label htmlFor="meta-horizonte">¿Para cuándo?</label>
          <Select
            id="meta-horizonte"
            label="¿Para cuándo?"
            value={horizon}
            onChange={(v) => setHorizon(v as Goal['horizon'])}
            options={[
              { value: 'trimestral', label: 'Este trimestre' },
              { value: 'anual', label: 'Este año' },
              { value: 'sin-fecha', label: 'Sin fecha' },
            ]}
          />
        </div>
        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={title.trim() === ''}
          onClick={() => {
            void upsertGoal({ ...(goal ? { id: goal.id } : {}), areaId, title, horizon });
            onClose();
          }}
        >
          {goal ? 'Guardar' : 'Añadir'}
        </button>
      </div>
    </>
  );
}

function EditorIdentidad({
  open,
  areaId,
  actual,
  onClose,
}: {
  open: boolean;
  areaId: string;
  actual: { id: string; text: string } | null;
  onClose: () => void;
}): ReactElement {
  return (
    <Sheet open={open} onClose={onClose} title="Quién quieres ser">
      <FormularioIdentidad
        key={`${areaId}:${actual?.id ?? 'nueva'}`}
        areaId={areaId}
        actual={actual}
        onClose={onClose}
      />
    </Sheet>
  );
}

function FormularioIdentidad({
  areaId,
  actual,
  onClose,
}: {
  areaId: string;
  actual: { id: string; text: string } | null;
  onClose: () => void;
}): ReactElement {
  const [text, setText] = useState(actual?.text ?? 'soy alguien que ');

  return (
    <>
      <div className="stack stack--loose">
        <p className="t-sm t-dim">
          En primera persona y en presente. No «quiero leer más», sino «soy alguien que lee». Es la
          frase que la app te va a devolver dentro de un año con los días de evidencia detrás.
        </p>
        <div className="field">
          <label htmlFor="identidad-texto">Tu frase</label>
          <textarea
            id="identidad-texto"
            className="textarea"
            style={{ minHeight: 80 }}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <p className="t-sm t-dim">
          Esta frase no sale de tu dispositivo. Ni al compartir tu año, ni en ninguna estadística.
        </p>
        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={text.trim().length < 4}
          onClick={() => {
            void upsertIdentity({ ...(actual ? { id: actual.id } : {}), areaId, text });
            onClose();
          }}
        >
          Guardar
        </button>
      </div>
    </>
  );
}
